import React, { useState, useEffect } from 'react';
import './css/LabContent.css';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRightLong, faChevronDown, faChevronUp, faPersonRunning, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import ludzik from './images/ludzik.png';
import NavigateSVG from './helpers/NavigateSVG';
import labService from './labService';
import authService from '../auth/authService';
import HashLoader from "react-spinners/HashLoader";


const LabContent = () => {
    const [expandedRows, setExpandedRows] = useState({});
    const [endpoints, setEndpoints] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [selectedEndpoints, setSelectedEndpoints] = useState([]);
    const [analLoading, setAnalLoading] = useState(true);
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [deleteID, setDeleteID] = useState(null);
    const [predictions, setPredictions] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'start_time', direction: 'descending' });



    const handleResultsClick = (id) => {
        navigate(`/laboratory/results/${id}`);
    };

    const handleRunningPred = (id) => {
        navigate(`/laboratory/computation/${id}`);
    };

    const openModal = (id) => {
        setDeleteID(id)
        setShowModal(true);
    };
    
    const closeModal = () => {
        setShowModal(false);
        setIsConfirmed(false); // Reset confirmation checkbox
    };

    const handleDelete = () => {
    if (currentUser && deleteID && isConfirmed) {
        labService.deleteAnalysis(deleteID, currentUser.access)
            .then(() => {
                // Remove the deleted prediction from the local state
                setPredictions(prevPredictions => 
                    prevPredictions.filter(pred => pred.id !== deleteID)
                );
                // navigate('/laboratory/'); // You probably don't need this if you're already on this page
            })
            .catch(error => console.error(error))
            .finally(() => closeModal());
    }
    };


    useEffect(() => {
        const user = authService.getCurrentUser();
        setCurrentUser(user);
    }, []);

    useEffect(() => {
    // If there is no user, don't do anything
    if (!currentUser) return;

    // 1. Define the fetching logic in a reusable function
    const fetchPredictions = () => {
        labService.getAnalyses(currentUser.access)
            .then(response => {
                setPredictions(response.data);
            })
            .catch(error => {
                console.error("Failed to fetch predictions:", error);
            });
    };

    // 2. Call it immediately so the user doesn't wait 10 seconds for the first load
    fetchPredictions();

    // 3. Set up the interval to run every 10 seconds (10000 milliseconds)
    const intervalId = setInterval(() => {
        fetchPredictions();
    }, 10000);

    // 4. CRITICAL: Cleanup function to clear the interval when the component unmounts
    return () => {
        clearInterval(intervalId);
    };
    }, [currentUser]); // Keep handleDelete OUT of this array!

    const sortedPredictions = [...predictions].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
    });

    const userEndpoints = sortedPredictions.map((anal) => anal.endpoint_type);

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicatorClass = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? 'ascending' : 'descending';
        }
        return '';
    };

    const setNewPredOpos = () => {
        navigate('/home');
    }
    
    useEffect(() => {
        if (currentUser) {
            labService.getEndpoints(currentUser.access)
                .then(response => {
                    setEndpoints(response.data)
                    setAnalLoading(false)
                })
                .catch(error => console.error(error));
        }
    }, [currentUser]);
    
    const toggleEndpointFilter = (endpoint) => {
        setSelectedEndpoints((prevSelected) => {
            if (prevSelected.includes(endpoint)) {
                return prevSelected.filter((e) => e !== endpoint);
            } else {
                return [...prevSelected, endpoint];
            }
        });
    };

    const filteredPredictions = selectedEndpoints.length > 0
    ? sortedPredictions.filter((anal) => selectedEndpoints.includes(anal.endpoint_type))
    : sortedPredictions;

    const toggleRowExpansion = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <div className='lab-content'>
            {showModal && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div className="trash-icon">
                                <FontAwesomeIcon icon={faTrashCan} size="2x" />
                            </div>
                            <button className="close-button" onClick={closeModal}>
                                ×
                            </button>
                        </div>
                        <div className='modal-con'>
                            <p className="p-delete">
                                This action will delete all data regarding your prediction.
                            <br/>
                                Your tokens <strong>will not be refunded</strong> in this case.
                            <br/>
                                Please confirm by checking the box below.
                            </p>

                            <label className='p-delete confirm-txt'>
                                <input
                                    type="checkbox"
                                    className='delete-check'
                                    checked={isConfirmed}
                                    onChange={(e) => setIsConfirmed(e.target.checked)}
                                />
                                I confirm that I want to delete this prediction result.
                            </label>
                            <div className="modal-buttons">
                                <button className={`confirm-btn ${isConfirmed ? '' : 'disabled-btn'}`} onClick={handleDelete} disabled={!isConfirmed}>
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            <div className='header-content'>
                <h2 className='pred-over'>Predictions overview</h2>
                <button className='new-pred' onClick={setNewPredOpos}>New prediction</button>  
            </div>
            <div className='endpoint-filters'>
                    {endpoints.map((endpoint, idx) => (
                        <button
                            key={idx}
                            className={`endpoint-filter-btn ${selectedEndpoints.includes(endpoint.id) ? 'selected' : ''}`}
                            onClick={() => userEndpoints.includes(endpoint.id) ? toggleEndpointFilter(endpoint.id) : null}
                            disabled={!userEndpoints.includes(endpoint.id)}
                        >
                            {endpoint.endpoint_name}
                        </button>
                    ))}
            </div>

            <div className='predictions-area'>
                {analLoading ? (
                    <div className='loader-div'>
                        <HashLoader className='loader' size={120} color='#0b5578' />
                    </div>
                ) : sortedPredictions.length === 0 ? (
                    <div className='center-ludzik'>
                        <img className="ludzik" src={ludzik} alt="ludzik" />
                        <p className='no-preds'>No predictions to display.</p>
                        <p className='no-preds'>Start a new analysis by clicking the <span className='bold'>New prediction</span> button</p>
                    </div>
                ) : (
                    <div className='div-table-preds'>
                        <table className='predictions-table'>
                            <thead>
                                <tr className='tr-first'>
                                    <th></th>
                                    <th onClick={() => handleSort('name')} className={getSortIndicatorClass('name')}><div className='arrow-height'>Name</div></th>
                                    <th onClick={() => handleSort('endpoint_type')} className={getSortIndicatorClass('endpoint_type')}><div className='arrow-height'>Endpoint</div></th>
                                    <th onClick={() => handleSort('model_name')} className={getSortIndicatorClass('model_name')}><div className='arrow-height'>Model</div></th>
                                    <th onClick={() => handleSort('status')} className={getSortIndicatorClass('status')}><div className='arrow-height'>Status</div></th>
                                    <th onClick={() => handleSort('start_time')} className={getSortIndicatorClass('start_time')}><div className='arrow-height'>Date</div></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPredictions.map((anal, idx) => (
                                    <React.Fragment key={anal.id}>
                                        
                                        <tr className={`tr-color ${idx % 2 === 0 ? 'odd-row' : ''}`}>
                                            <td style={{ width: '5%' }}>
                                                {<FontAwesomeIcon
                                                        className='cursor trash'
                                                        icon={faTrashCan}
                                                        onClick={() => openModal(anal.id)}
                                                    />}
                                            </td>
                                            <td style={{ width: '20%' }}>{anal.name}</td>
                                            <td style={{ width: '15%' }}>{anal.endpoint_type_name}</td>
                                            
                                            <td style={{ width: '30%' }}> 
                                                {anal.anal_status === 'Running' ? (
                                                    "Running selected"
                                                ) : (
                                                    anal.selected_models_names.length === 0 ? (
                                                        "Not yet selected"
                                                    ) : anal.selected_models_names.length === 1 ? (
                                                        anal.selected_models_names[0]
                                                    ) : (
                                                        <>
                                                            Multiple
                                                            <button
                                                                onClick={() => toggleRowExpansion(anal.id)}
                                                                className="expand-btn"
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={expandedRows[anal.id] ? faChevronUp : faChevronDown}
                                                                />
                                                            </button>
                                                        </>
                                                    )
                                                )}
                                            </td>

                                            <td style={{ width: '12.5%' }} className={`td-stat ${anal.anal_status.toLowerCase()}`}>
                                                {anal.anal_status.toUpperCase()}
                                            </td>
                                            <td style={{ width: '12.5%' }}>{new Date(anal.start_time).toLocaleDateString('en-GB')}</td>
                                            <td style={{ width: '5%' }}>
                                                {anal.anal_status.toLowerCase() === 'initialised' ? (
                                                    <NavigateSVG className="table-icons" id={anal.id} />
                                                ) : anal.anal_status.toLowerCase() === 'running' ? (
                                                    <FontAwesomeIcon
                                                        className="table-icons"
                                                        icon={faPersonRunning}
                                                        onClick={() => handleRunningPred(anal.id)}
                                                    />
                                                ) : anal.anal_status.toLowerCase() === 'done' ? (
                                                    <FontAwesomeIcon
                                                        className='cursor table-icons'
                                                        icon={faRightLong}
                                                        onClick={() => handleResultsClick(anal.id)}
                                                    />
                                                ): (
                                                    <svg className='svg-main-fail' fill="#000000" viewBox="0 -0.5 17 17" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path fill-rule="evenodd" d="M15.35 8c0 3.377-2.945 6.25-6.75 6.25S1.85 11.377 1.85 8 4.795 1.75 8.6 1.75 15.35 4.623 15.35 8zm1.25 0c0 4.142-3.582 7.5-8 7.5S.6 12.142.6 8C.6 3.858 4.182.5 8.6.5s8 3.358 8 7.5zM9.229 3.101l-.014 7.3-1.25-.002.014-7.3 1.25.002zm.016 9.249a.65.65 0 1 0-1.3 0 .65.65 0 0 0 1.3 0z"></path></g></svg>
                                                )
                                                }
                                            </td>
                                        </tr>
                                        {expandedRows[anal.id] && (
                                            <>
                                                {anal.anal_status.toLowerCase() === 'done' ? (
                                                    anal.predictions.map((prediction, idx) => (
                                                        <tr className="expanded-row" key={idx}>
                                                            <td colSpan="3"></td> {/* Empty cells for Name and Date */}

                                                            {/* Model Name Column */}
                                                            <td colSpan="1">
                                                                <div className='endpoint-list'>
                                                                    <p className='expand-p'>{prediction.model_name}</p>
                                                                </div>
                                                            </td>

                                                            {/* Status SVG Column */}
                                                            <td colSpan="1">
                                                                <div className='endpoint-list'>
                                                                    <div className="prediction-item">
                                                                        {prediction.status === 'Done' ? (
                                                                            <svg className='svg-status' viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                <circle cx="12" cy="12" r="10" stroke="#73a149" strokeWidth="1.5"></circle>
                                                                                <path d="M8.5 12.5L10.5 14.5L15.5 9.5" stroke="#73a149" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                                                            </svg>
                                                                        ) : prediction.status === 'Failed' ? (
                                                                            <svg className='svg-status svg-status-fail' fill="#000000" viewBox="0 -0.5 17 17" xmlns="http://www.w3.org/2000/svg">
                                                                                <path fillRule="evenodd" d="M15.35 8c0 3.377-2.945 6.25-6.75 6.25S1.85 11.377 1.85 8 4.795 1.75 8.6 1.75 15.35 4.623 15.35 8zm1.25 0c0 4.142-3.582 7.5-8 7.5S.6 12.142.6 8C.6 3.858 4.182.5 8.6.5s8 3.358 8 7.5zM9.229 3.101l-.014 7.3-1.25-.002.014-7.3 1.25.002zm.016 9.249a.65.65 0 1 0-1.3 0 .65.65 0 0 0 1.3 0z"></path>
                                                                            </svg>
                                                                        ) : null}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td colSpan="3"></td> 
                                                        </tr>
                                                    ))
                                                ) : anal.anal_status.toLowerCase() === 'initialised' ? (
                                                    anal.selected_models_names.map((modelName, idx) => (
                                                        <tr className="expanded-row" key={idx}>
                                                            <td colSpan="3"></td> 

                                                            {/* Model Name Column */}
                                                            <td colSpan="1">
                                                                <div className='endpoint-list'>
                                                                    <p className='expand-p'>{modelName}</p>
                                                                </div>
                                                            </td>

                                                            <td colSpan="4"></td> 
                                                        </tr>
                                                    ))
                                                ) : null}
                                            </>
                                        )}


                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LabContent;
